import React, { Suspense, useEffect } from "react";
import { HashRouter as Router, Routes, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import { loadUser } from "./actions";
import "./App.css";
import ProtectedRoute from "./Routes/ProtectedRoute.jsx";
import FeedbackWidget from "./components/common/FeedbackWidget.jsx";
import { lazyWithRetry } from "./utils/lazyWithRetry";

const Layout = lazyWithRetry(() => import("./components/layout/Layout"));
const Login = lazyWithRetry(() => import("./components/auth/Login.jsx"));
const ForgotPassword = lazyWithRetry(() => import("./components/auth/ForgotPassword.jsx"));
const Home = lazyWithRetry(() => import("./components/public/Home.jsx"));
const MyProfile = lazyWithRetry(() => import("./components/common/MyProfile.jsx"));
const StudentAttendance = lazyWithRetry(() => import("./components/student/Attendance.jsx"));
const StudentDashboard = lazyWithRetry(() => import("./components/student/Dashboard.jsx"));
const StudentFees = lazyWithRetry(() => import("./components/student/Fees.jsx"));
const StudentHomework = lazyWithRetry(() => import("./components/student/Homework.jsx"));
const StudentMarks = lazyWithRetry(() => import("./components/student/Marks.jsx"));
const StudentNotices = lazyWithRetry(() => import("./components/student/Notices.jsx"));
const StudentSports = lazyWithRetry(() => import("./components/student/Sports.jsx"));
const StudentStudyMaterials = lazyWithRetry(() => import("./components/student/StudyMaterials.jsx"));
const StudentTimetable = lazyWithRetry(() => import("./components/student/Timetable.jsx"));
const StudentTransport = lazyWithRetry(() => import("./components/student/Transport.jsx"));

// role-guard helper — matches the original monolith's App.jsx exactly
const A = (role, C) => (
  <ProtectedRoute role={role}>
    <C />
  </ProtectedRoute>
);

function SplashScreen() {
  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "3px solid #E2E8F0",
          borderTopColor: "#4F46E5",
          animation: "ochrelRingSpin 0.7s linear infinite",
        }}
      />
    </div>
  );
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incomingToken = params.get("token");
    if (incomingToken) {
      localStorage.setItem("token", incomingToken);
      params.delete("token");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash);
    }
    dispatch(loadUser());
  }, [dispatch]);

  const { loading } = useSelector((s) => s.auth);

  if (loading) return <SplashScreen />;

  return (
    <Router>
      <Toaster position="top-right" />
      <Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>Loading…</div>}>
      <Layout>
        <FeedbackWidget />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/student" element={A("student", StudentDashboard)} />
          <Route path="/student/profile" element={A("student", MyProfile)} />
          <Route path="/student/attendance" element={A("student", StudentAttendance)} />
          <Route path="/student/marks" element={A("student", StudentMarks)} />
          <Route path="/student/fees" element={A("student", StudentFees)} />
          <Route path="/student/homework" element={A("student", StudentHomework)} />
          <Route path="/student/timetable" element={A("student", StudentTimetable)} />
          <Route path="/student/materials" element={A("student", StudentStudyMaterials)} />
          <Route path="/student/transport" element={A("student", StudentTransport)} />
          <Route path="/student/notices" element={A("student", StudentNotices)} />
          <Route path="/student/sports" element={A("student", StudentSports)} />
        </Routes>
      </Layout>
      </Suspense>
    </Router>
  );
}

export default App;
